import {Component, inject, input, model, output, signal, ViewEncapsulation} from '@angular/core';
import {DxDataGridModule, DxPaginationModule, DxPopupModule, DxTextBoxModule} from 'devextreme-angular';
import {SelectionChangedEvent} from 'devextreme/ui/data_grid';
import {ValueChangedEvent} from 'devextreme/ui/text_box';
import {SharedModule} from '../../../../shared/shared.module';
import {ButtonThemeEnum} from '../../../../shared/ui/button/button-theme-enum';
import {PlcProduitComplementaireDTO} from '../../../../core/dtos/plc-produit-complementaire-dto';
import {
  SelectionPlcProduitComplementaire
} from '../../../../core/models/gestion-produits/declinaison/complementaire/produit/selection-plc-produit-complementaire';
import {PRODUIT_COMPLEMENTAIRE_PAGE_SIZE} from '../../../../core/services/entities/plc-produit-complementaire.service';
import {
  ProduitProduitComplementaireService
} from '../../../../core/services/entities/produit-produit-complementaire.service';

/**
 * Choix des points de livraison à associer au produit décliné complémentaire courant. Miroir de la sélection de
 * produits proposée depuis le point de livraison : seuls les points encore associables sont listés.
 */
@Component({
  selector: 'yo-dialog-selection-plc',
  templateUrl: './dialog-selection-plc.component.html',
  styleUrls: ['./dialog-selection-plc.component.scss'],
  standalone: true,
  imports: [DxPopupModule, DxDataGridModule, DxPaginationModule, DxTextBoxModule, SharedModule],
  encapsulation: ViewEncapsulation.None
})
export class DialogSelectionPlcComponent {

  readonly PAGE_SIZE = PRODUIT_COMPLEMENTAIRE_PAGE_SIZE;
  readonly ButtonThemeEnum = ButtonThemeEnum;

  readonly visible = model<boolean>(false);

  readonly produitDeclinaisonId = input<number>();

  readonly produitLibelle = input<string>('');

  readonly selectedPlcIdList = input<number[]>([]);

  readonly selectionValidee = output<SelectionPlcProduitComplementaire>();

  private readonly produitComplementaireSvc = inject(ProduitProduitComplementaireService);

  readonly plcAssociableList = this.produitComplementaireSvc.plcAssociableList;
  readonly plcAssociableTotal = this.produitComplementaireSvc.plcAssociableTotalSignal;
  readonly plcAssociablePage = this.produitComplementaireSvc.plcAssociablePageSignal;

  readonly currentPlcIdList = signal<number[]>([]);

  /** Lignes retenues, mémorisées page après page : la grille ne connaît que la page affichée. */
  private readonly ligneByPlcIdMap = new Map<number, PlcProduitComplementaireDTO>();

  onShowing = (): void => {
    this.currentPlcIdList.set([...this.selectedPlcIdList()]);
    this.ligneByPlcIdMap.clear();
    this.produitComplementaireSvc.openPlcAssociable(this.produitDeclinaisonId());
  };

  /** Libère la liste : inutile de la garder chargée dialogue fermé. */
  onHiding = (): void => this.produitComplementaireSvc.closePlcAssociable();

  onSearchChanged = (event: ValueChangedEvent): void =>
    this.produitComplementaireSvc.searchPlcAssociable(event.value ?? '');

  onPageIndexChange = (page: number): void => this.produitComplementaireSvc.goToPlcAssociablePage(page);

  /**
   * La grille ne connaît que la page courante : la sélection des autres pages est conservée telle quelle,
   * seules les lignes visibles suivent les cases à cocher.
   */
  onSelectionChanged = (event: SelectionChangedEvent): void => {
    (event.selectedRowsData ?? []).forEach((plc: PlcProduitComplementaireDTO) => this.ligneByPlcIdMap.set(plc.plcId, plc));

    const pagePlcIdList = this.plcAssociableList().map(plc => plc.plcId);
    const selectedPlcIdSet = new Set((event.selectedRowKeys ?? []) as number[]);

    const plcIdList = [
      ...this.currentPlcIdList().filter(plcId => !pagePlcIdList.includes(plcId)),
      ...pagePlcIdList.filter(plcId => selectedPlcIdSet.has(plcId))
    ];
    if (!this.isSameSelection(plcIdList)) {
      this.currentPlcIdList.set(plcIdList);
    }
  };

  private isSameSelection = (plcIdList: number[]): boolean => {
    const currentPlcIdList = this.currentPlcIdList();
    return currentPlcIdList.length === plcIdList.length && plcIdList.every(plcId => currentPlcIdList.includes(plcId));
  };

  valider = (): void => {
    const plcIdList = this.currentPlcIdList();
    this.selectionValidee.emit({
      plcIdList,
      plcList: plcIdList.map(plcId => this.ligneByPlcIdMap.get(plcId)).filter(plc => !!plc)
    });
    this.visible.set(false);
  };

  fermer = (): void => this.visible.set(false);
}
